import { useCallback, useEffect, useState } from 'react'

import { getTripDayProgram } from '../../config/tripChallenges'
import { dailyPhotoPhase } from '../../domain/dailyPhoto'
import { journeyMoment } from '../../domain/journeyState'
import { tripDateKey } from '../../domain/tripTime'
import { subscribeSyncRuntime } from '../../services/syncRuntime'
import { sessionStore } from '../access/sessionStore'
import { useAdminView } from '../access/useAdminView'
import { albumRepository } from '../album/albumService'
import { challengeRepository } from '../group/challengeService'
import { pollRepository } from '../group/pollService'
import { selectActionableTabs } from './actionableTabs'

export function useActionableTabs(): ReadonlySet<string> {
  const session = sessionStore((state) => state.session)
  const isAdminView = useAdminView()
  const [tabs, setTabs] = useState<ReadonlySet<string>>(() => new Set())

  const refresh = useCallback(() => {
    if (!session) { setTabs(new Set()); return }
    const now = new Date()
    const moment = journeyMoment(now)
    if (moment.phase === 'before' || moment.phase === 'after') { setTabs(new Set()); return }
    const productDay = tripDateKey(now)
    void Promise.all([
      albumRepository.listPhotos(session.tripId),
      challengeRepository.listChallengesFor(session.memberId, isAdminView, now),
      challengeRepository.listCompletionsFor(session.memberId, now),
      pollRepository.listPollViews(session.memberId, isAdminView, now),
    ]).then(([photos, challenges, completions, polls]) => {
      const program = getTripDayProgram(productDay)
      const ownPhoto = photos.some((photo) => photo.purpose === 'daily_candidate' && photo.productDay === productDay && photo.memberId === session.memberId)
      const dailyChallenge = challenges.find((challenge) => challenge.productDay === productDay && challenge.contentRole === 'daily_challenge')
      const ownChallenge = dailyChallenge ? completions.some((completion) => completion.challengeId === dailyChallenge.id && completion.memberId === session.memberId) : false
      setTabs(selectActionableTabs({
        duringTrip: true,
        photoDue: program?.media.kind === 'photo' && dailyPhotoPhase(productDay, now) === 'open' && !ownPhoto,
        challengeDue: dailyChallenge?.phase === 'active' && !ownChallenge,
        pollDue: polls.some((poll) => poll.status === 'open' && !poll.hasVoted),
      }))
    }).catch(() => setTabs(new Set()))
  }, [isAdminView, session])

  useEffect(() => {
    const initial = window.setTimeout(refresh, 0)
    const timer = window.setInterval(refresh, 60_000)
    const unsubscribe = subscribeSyncRuntime(refresh)
    return () => { window.clearTimeout(initial); window.clearInterval(timer); unsubscribe() }
  }, [refresh])

  return tabs
}
